import React, { useRef } from "react";

import axios from "axios";
import clsx from "clsx";

import CircularProgress from "@busbud/design-system-components/dist/CircularProgress/CircularProgress";
import { Button } from "@busbud/horizon";

import { useWhitelabelContext } from "@app/context/whitelabel-context";
import { toBusbudError } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import { useScript } from "@app/helpers/use-script";
import type { NapiUser } from "@app/types/napi-types";

import type { ConnectButtonProps } from "../props";

import { useStyles } from "./google-connect-button.styles";
import { GoogleLogo } from "./google-logo";
import { SDK_ERRORS_TO_IGNORE } from "../utils/constants";
import {
  trackClickedConnectSocial,
  trackConnectSocialError
} from "../utils/tracking";

const PROVIDER = "google";

const GOOGLE_CLIENT_IDS = {
  default:
    "806409037727-d287t2krhpdtm9ioiggh3jht3rogmod6.apps.googleusercontent.com",
  neredennereye:
    "76038753374-u0gqhtemgld36vdosfa2mpknu53ftdg5.apps.googleusercontent.com"
};

export const GoogleConnectButton: React.FC<ConnectButtonProps> = ({
  setInProgress,
  onSuccess,
  onError,
  is_sign_in = true,
  is_email_opt_in = false,
  label,
  class_name,
  loading = false
}) => {
  const { tracker, translator, geoData, user_country_code } = useAppContext();
  const { whitelabel } = useWhitelabelContext();
  const whitelabel_name = whitelabel?.name;
  const google_button = useRef(null);
  const { classes } = useStyles();
  const auto_enrol_new_user =
    geoData.allowsImplicitConsentForMarketingEmails(user_country_code);

  useScript("https://apis.google.com/js/api:client.js", () => {
    window.gapi.load("auth2", () => {
      const g_id =
        GOOGLE_CLIENT_IDS[
          (whitelabel_name as keyof typeof GOOGLE_CLIENT_IDS) || "default"
        ] || GOOGLE_CLIENT_IDS.default;

      const auth2 = window.gapi.auth2.init({
        client_id: g_id,
        cookiepolicy: "single_host_origin",
        plugin_name: "busbud_connect"
      });

      auth2.attachClickHandler(
        google_button.current,
        {},
        onAuthSuccess,
        onAuthFailure
      );
    });
  });

  async function onAuthSuccess(googleUser: any) {
    try {
      const user = await axios.post<NapiUser>("/auth/signin-google", {
        id_token: googleUser.getAuthResponse().id_token,
        email_opt_in: is_email_opt_in,
        auto_enrol_new_user
      });

      const user_google_id = googleUser.getBasicProfile().getId();
      // @ts-expect-error
      tracker.identify({ google_id: user_google_id }, () =>
        onSuccess(user.data, PROVIDER)
      );
    } catch (err) {
      setInProgress(false);
      onAuthFailure(err);
    }
  }

  function onAuthFailure(err: Error | null) {
    trackConnectSocialError(
      tracker,
      PROVIDER,
      SDK_ERRORS_TO_IGNORE,
      is_sign_in,
      err
    );
    setInProgress(false);
    onError(toBusbudError(err)!);
  }

  return (
    <Button
      appearance="default"
      ref={google_button}
      variant="contained"
      size="lg"
      iconStart={
        loading ? (
          <CircularProgress size="sm" color="quaternary" />
        ) : (
          <GoogleLogo />
        )
      }
      className={clsx(class_name, classes.button, "w-full shadow-border-sm")}
      onClick={() => {
        setInProgress(true);
        trackClickedConnectSocial(
          tracker,
          is_sign_in,
          PROVIDER,
          auto_enrol_new_user
        );
      }}
    >
      {label || translator.t("!account.authentication.google")}
    </Button>
  );
};
